import React, { useContext, useEffect, useState } from 'react';
import { ContentCloudPermission, ContentCloudService } from '../shared-permissions';
import { ContentCloudComponentProps, withContentCloud } from '../WithContentCloud';
import { CONTENT_CLOUD_API_VERSION } from '../content-cloud-helper';
import { Badge, Col, Row } from 'react-bootstrap';
import { ApiContext, PagedList } from '../../../../common';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/pro-light-svg-icons/faChevronRight';
import { HeaderCol, LoadingBar, Right } from '@edgebox/react-components';
import { EntryRelativeDate } from '../Components/EntryRelativeDate';
import { PublisherClient, SystemClient } from '../PublisherClient';
import { useParams } from 'react-router';
import { ClientEntryPage } from './ClientEntryPage';
import { AppContext } from '../../../../common/contexts/AppContext';
import { PrimaryActionHighlightBox } from '../../../Shared/PrimaryActionHighlightBox';
import { PrimaryActionButton } from '../../../Shared/PrimaryActionButton';
import { AddClientForm } from './Components/AddClientForm';
import { INavigateProp, withNavigate } from '../../../RouterHelper';

const ENTRIES_PER_PAGE = 50;

interface Props extends ContentCloudComponentProps, INavigateProp {}

function ClientCollection({ contentCloudData, navigate }: Props) {
  const { space, environment, contentCloud } = contentCloudData ?? {};
  const api = useContext(ApiContext);

  const app = useContext(AppContext);
  const { project } = app;

  const [publisherClient, setPublisherClient] = useState<PublisherClient | null>(null);
  useEffect(() => {
    (async () => {
      if (!api || !contentCloud?.id || !project || !environment?.id) {
        return;
      }

      const accessToken = await api.syndication.remoteServices.getContentCloudRequestToken(
        contentCloud.id,
        project.id,
        {
          permissions: [ContentCloudPermission.SPACE_READ, ContentCloudPermission.CLIENT_READ, ContentCloudPermission.CLIENT_WRITE],
          services: [ContentCloudService.PUBLISHER],
        },
        {
          environmentIds: [environment.id],
        }
      );

      setPublisherClient(
        new PublisherClient({
          baseUrl: contentCloud.baseUrl,
          version: CONTENT_CLOUD_API_VERSION,
          accessToken,
        })
      );
    })();
  }, [api, contentCloud?.id, contentCloud?.baseUrl, environment?.id, project]);

  const [createClient, setCreateClient] = useState(false);

  const param = useParams<{ id: string }>();
  if (param.id) {
    return <ClientEntryPage id={param.id} />;
  }

  if (!publisherClient) {
    return <LoadingBar />;
  }

  return (
    <div>
      <PagedList<SystemClient, {}>
        renderListHeader={() => (
          <Row>
            <HeaderCol xs={5}>
              <div className="ps-3">Name</div>
            </HeaderCol>
            <HeaderCol xs={3}>Identifier</HeaderCol>
            <HeaderCol xs={2}>Updated</HeaderCol>
            <HeaderCol xs={2}>Status</HeaderCol>
          </Row>
        )}
        renderItem={(clientEntry) => (
          <Link
            to={`/content-cloud/clients/${clientEntry.id}`}
            className={'text-black ps-3 pe-1 pt-1 pb-1 d-block rounded bg-white mb-2 hover-shadow text-decoration-none'}
            key={clientEntry.id}
          >
            <Row className="m-0 p-0">
              <Col xs={5} className="ms-0 me-0 pt-2">
                <span className="fw-bold pt-1">{clientEntry.name}</span>
              </Col>
              <Col xs={3} className="ms-0 me-0 pt-2">
                {clientEntry.id}
              </Col>
              <Col xs={2} className="ms-0 me-0 pt-2">
                {clientEntry.sys.versionCreatedAt ? <EntryRelativeDate date={clientEntry.sys.versionCreatedAt} /> : null}
              </Col>
              <Col xs={1} className="ms-0 me-0 pt-2">
                {clientEntry.sys.isPublished ? <Badge bg="success">active</Badge> : <Badge bg="danger">inactive</Badge>}
              </Col>
              <Col xs={1} className="text-end fs-4 text-light pt-1">
                <FontAwesomeIcon icon={faChevronRight} />
              </Col>
            </Row>
          </Link>
        )}
        request={async (page, filter) => {
          const response = await publisherClient.getClients();

          return {
            items: response,
            itemsPerPage: ENTRIES_PER_PAGE,
            numberOfPages: 1,
            page: 0,
            totalNumberOfItems: response.length,
          };
        }}
      />

      <div className="my-5">
        <PrimaryActionHighlightBox>
          {createClient ? (
            <>
              {space && environment && publisherClient && (
                <AddClientForm
                  onSave={async (draft) => {
                    console.log('saving new client', draft);
                    const client = await publisherClient.mutateClient(draft);
                    console.log('saved new client', client);

                    if (client) {
                      navigate(`/content-cloud/clients/${client.id}`);
                    }
                  }}
                  spaceId={space.id}
                >
                  <Right className="p-3 pb-0 mt-2">
                    <PrimaryActionButton type="submit">Create</PrimaryActionButton>
                  </Right>
                </AddClientForm>
              )}
            </>
          ) : (
            <div>
              <h2 className="m-0">Create client</h2>
              <div>
                Use clients to sign JWTs and authorize requests. Please check out the documentation if you are not familiar with clients
                yet.
              </div>
              <PrimaryActionButton className="mt-4" onClick={() => setCreateClient(true)}>
                Create client
              </PrimaryActionButton>
            </div>
          )}
        </PrimaryActionHighlightBox>
      </div>
    </div>
  );
}

export default withNavigate(withContentCloud(ClientCollection, [ContentCloudPermission.SPACE_READ, ContentCloudPermission.CLIENT_READ]));
